import { absTimeDifference, timeDifference } from "utils/timeDifference";
import { Row, Col } from "reactstrap";
import { useFieldArray } from "react-hook-form";
import { useState, useRef, useEffect } from "react";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { FormCheck, ImageModal, FormEntry } from "components";
import { FiX } from "react-icons/fi";
import { getDataURIMimeType, mimeTypes } from "utils/getDataURIMimeType";
import { openBase64InNewTab } from "utils/openBase64InNewTab";

const initialImageModalProps = {
  open: false,
  index: null,
};

export const FirstPage = ({
  register,
  errors,
  lessonDetail,
  getValues,
  studentPrefix,
  watch,
  control,
}) => {
  const {
    fields: urgentBonusProofImages,
    append: appendUrgentBonusProof,
    remove: removeUrgentBonusProof,
  } = useFieldArray({
    control,
    name: "urgentBonusProof",
  });

  const urgentBonusProofInputRef = useRef(null);

  const [imageModalProps, setImageModalProps] = useState(
    initialImageModalProps,
  );

  const appendFileToUploadedImg = async (e) => {
    if (!e.target.files.length) return;

    const imgURI = await encodeImageFileAsURL(e.target.files[0]);
    appendUrgentBonusProof({ data: imgURI });
    e.target.value = "";
  };

  const removeFileFromUploadedImg = async (index) =>
    removeUrgentBonusProof(index);

  // useEffect(() => {
  // 	if (lessonDetail?.isUrgentClass) {
  // 		register(`urgentBonusProof`, {
  // 			required: true
  // 				? {
  // 						value: true,
  // 						message: `Urgent Bonus Proof is required`,
  // 				  }
  // 				: { value: false },
  // 		});
  // 	}
  // }, [lessonDetail]);

  return (
    <>
      <ImageModal
        open={imageModalProps.open}
        hideModal={() => setImageModalProps(initialImageModalProps)}
      >
        <img
          src={urgentBonusProofImages[imageModalProps.index]?.data || ""}
          alt=""
        />
      </ImageModal>

      <Row>
        <Col md={6} className="mb-2">
          <FormEntry
            formLabel="Lesson Start Time"
            type="time"
            name="startTime"
            required={true}
            register={register}
            prefix={studentPrefix}
            readOnly
          />
        </Col>
        <Col md={6} className="mb-2">
          <FormEntry
            formLabel="Lesson End Time"
            type="time"
            name="endTime"
            register={register}
            prefix={studentPrefix}
            readOnly
          />
        </Col>
      </Row>
      <FormEntry
        register={register}
        formLabel="Preparation Start Time"
        type="time"
        name="preparationStartTime"
        required={true}
        validation={{
          validate: {
            checkDifferenceFromLessonStartLessonThan60: (
              preparationStartTime,
            ) =>
              timeDifference(preparationStartTime, lessonDetail.startTime) >=
                0 || "Preparation end time must be before lesson start time",
          },
        }}
        error={errors.preparationStartTime}
      />
      <FormEntry
        register={register}
        formLabel="Preparation End Time"
        type="time"
        name="preparationEndTime"
        required={true}
        validation={{
          validate: {
            checkMinMax: (preparationEndTime) =>
              timeDifference(
                getValues("preparationStartTime"),
                preparationEndTime,
              ) >= 0 ||
              "Preparation end time must be after preparation start time",
            checkDifference: (preparationEndTime) =>
              absTimeDifference(
                getValues("preparationStartTime"),
                preparationEndTime,
              ) <=
                absTimeDifference(
                  lessonDetail.startTime,
                  lessonDetail.endTime,
                ) /
                  2 ||
              `Preparation end time must be within ${absTimeDifference(lessonDetail.startTime, lessonDetail.endTime) / 2} minutes of preparation start time`,
            checkDifferenceFromLessonStartMoreThanZero: (preparationEndTime) =>
              timeDifference(preparationEndTime, lessonDetail.startTime) >= 0 ||
              "Preparation end time must be before lesson start time",
          },
        }}
        error={errors.preparationEndTime}
      />
      <FormEntry
        register={register}
        formLabel="Report Start Time"
        type="time"
        name="reportStartTime"
        required={true}
        validation={{
          validate: {
            checkDifferenceFromEndTime: (reportStartTime) =>
              timeDifference(lessonDetail?.endTime, reportStartTime, true) >=
                0 || "Report start time must be after class end time",
          },
        }}
        error={errors.reportStartTime}
      />
      <FormEntry
        register={register}
        formLabel="Report End Time"
        type="time"
        name="reportEndTime"
        required={true}
        validation={{
          validate: {
            checkMinMax: (reportEndTime) => {
              return (
                timeDifference(getValues("reportStartTime"), reportEndTime) >=
                  0 || "Report end time must be after report start time"
              );
            },
            checkDifferenceFromEndTime: (reportEndTime) => {
              return (
                timeDifference(lessonDetail?.endTime, reportEndTime) >= 0 ||
                "Report end time must be after class end time"
              );
            },
            checkDifference: (reportEndTime) =>
              absTimeDifference(getValues("reportStartTime"), reportEndTime) <=
                30 ||
              "Report end time must be within 30 minutes of report start time",
          },
        }}
        error={errors.reportEndTime}
      />
      <FormEntry
        formLabel="Course"
        type="text"
        name="courseName"
        register={register}
        prefix={studentPrefix}
      />
      <FormEntry
        formLabel="Lesson"
        type="text"
        name="customLessonName"
        register={register}
        prefix={studentPrefix}
        readOnly={
          Boolean(lessonDetail.lesson) &&
          !lessonDetail.isCustomLesson &&
          !lessonDetail?.lesson?.isCustomLesson
        }
        required={true}
        error={errors.customLessonName}
      />
      <div
        className="px-3 py-2 pb-4 rounded-lg"
        style={{ border: "1px solid #ADB5DB" }}
      >
        <h5 className=" font-weight-bold text-white mb-1">
          Urgent Bonus Proof <small className="text-info ml-2">*Optional</small>
        </h5>
        <input
          type="file"
          className="form-control d-none"
          onChange={appendFileToUploadedImg}
          accept="application/pdf, image/*"
          ref={urgentBonusProofInputRef}
        />
        {errors.studentReport?.urgentBonusProof && (
          <small className="text-danger">
            {errors.studentReport?.urgentBonusProof?.message}
          </small>
        )}
        <div className="mt-3">
          <small
            className="border border-light rounded px-3 py-2 text-light"
            style={{ cursor: "pointer" }}
            onClick={() => {
              urgentBonusProofInputRef.current?.click();
            }}
          >
            Click to upload
          </small>
        </div>

        {Boolean(urgentBonusProofImages.length) && (
          <div className="row mt-3 ">
            {urgentBonusProofImages.map((resource, index) => (
              <div className="col-4" key={resource.id}>
                <span
                  className="border border-danger"
                  style={{ cursor: "pointer", borderRadius: 5 }}
                  onClick={() => removeFileFromUploadedImg(index)}
                >
                  <FiX className="text-danger" />
                  <small className="text-danger mx-1">Remove</small>
                </span>
                <div className="mt-1">
                  <small>
                    {getDataURIMimeType(resource.data) !== mimeTypes.pdf ? (
                      <img
                        src={resource.data}
                        className="img-fluid rounded text-white"
                        alt="Resource Uploaded"
                        onClick={() =>
                          setImageModalProps({
                            open: true,
                            index,
                          })
                        }
                      />
                    ) : (
                      <span
                        onClick={() => openBase64InNewTab(resource.data)}
                        style={{
                          cursor: "pointer",
                          borderRadius: 5,
                          padding: 2,
                        }}
                        className="border border-success text-success px-1"
                      >
                        View Resource {index + 1}
                      </span>
                    )}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
